// iter150AZ.112 — Web Serial helper for the DR404 configurator.
//
// iter150AZ.120 — Fixed a critical bug in the previous implementation
// where `readUntil` raced `reader.read()` against a setTimeout. Because
// `Promise.race` doesn't cancel the losing promise, the in-flight
// `reader.read()` would stay queued and silently consume incoming
// bytes that were never delivered to our control flow. Cable RX-LEDs
// blinked but `_buffer` stayed empty.
//
// New architecture:
//   * `open()` spawns ONE long-running ``_readPump`` that calls
//     `reader.read()` exactly once at a time, appends every chunk to
//     `_buffer`, hex-logs it, and wakes any pending `readUntil`
//     waiter.
//   * `readUntil` no longer touches the reader directly. It evaluates
//     the predicate against `_buffer`, and when it doesn't match, it
//     waits for either (a) the pump to notify "new data" or
//     (b) the timeout, whichever happens first. No race, no lost reads.
//   * `close()` flips `_closed=true`, wakes all waiters, awaits
//     `reader.cancel()` (which terminates the pump), releases locks
//     and closes the port.

const TEXT_DECODER = new TextDecoder("utf-8", { fatal: false });
const TEXT_ENCODER = new TextEncoder();

// Order matters: 9600 first because the USR DR404 ships at 9600-8-N-1
// out of the factory and most field units never have their UART
// changed.  Older firmware (and units returning from RMA) sometimes
// boot at 57600, and a couple of OEM variants run 115200.
export const COMMON_BAUD_RATES = [9600, 57600, 115200, 38400, 19200];

/** True iff the active browser tab exposes the Web Serial API. */
export function isWebSerialSupported() {
  return typeof navigator !== "undefined"
      && !!navigator.serial
      && typeof navigator.serial.requestPort === "function";
}


/**
 * Sleep for ``ms`` milliseconds. ``signal`` (an AbortSignal) lets the
 * caller cancel a long timeout when the user presses Cancel.
 */
export function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal && signal.aborted) return reject(new Error("aborted"));
    const t = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => { clearTimeout(t); reject(new Error("aborted")); };
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });
}


/**
 * High-level wrapper around a single SerialPort. Owns its reader /
 * writer lifecycle, runs a persistent background read pump, and
 * exposes ``readUntil`` / ``sendAt`` / ``enterAtMode`` /
 * ``pollUntilAtMode`` on top of an internal append-only buffer.
 */
export class Dr404Serial {
  constructor({ port, baudRate, log }) {
    this.port = port;
    this.baudRate = baudRate;
    this.log = log || (() => {});
    this._reader  = null;
    this._writer  = null;
    this._buffer  = "";
    this._closed  = false;
    this._waiters = [];   // callbacks invoked when new data arrives
    this._pumpDone = null;
  }

  static async request() {
    if (!isWebSerialSupported()) {
      throw new Error("Web Serial API not available in this browser");
    }
    const port = await navigator.serial.requestPort();
    return port;
  }

  async open(baudRate = 9600) {
    this.baudRate = baudRate;
    await this.port.open({
      baudRate,
      dataBits: 8,
      stopBits: 1,
      parity: "none",
      flowControl: "none",
    });
    // Deassert DTR/RTS so the open() call doesn't cause a DTR pulse
    // that would reset USB-TTL bridges wired with the Arduino-style
    // DTR-RST capacitor convention. iter150AZ.114
    try {
      if (typeof this.port.setSignals === "function") {
        await this.port.setSignals({
          dataTerminalReady: false,
          requestToSend: false,
        });
      }
    } catch (e) {
      this.log(`[serial] setSignals not supported (${e.message || e}) — continuing`);
    }
    this._reader = this.port.readable.getReader();
    this._writer = this.port.writable.getWriter();
    this._closed = false;
    this._pumpDone = this._startReadPump();
    this.log(`[serial] opened @ ${baudRate} (DTR/RTS deasserted)`);
  }

  /**
   * Persistent background reader. Recovers from non-fatal stream
   * errors (framing / parity / buffer overrun) — which the DR404
   * happily emits during the post-RELD boot transition — by
   * releasing the dead reader and reacquiring a new one from
   * ``port.readable``. iter150AZ.123
   *
   * The pump exits only when:
   *   * ``_closed`` flips true (via ``close()`` calling
   *     ``reader.cancel()`` which resolves the pending ``read()``
   *     with ``{done: true}``).
   *   * ``port.readable`` becomes null (port physically disconnected).
   */
  _startReadPump() {
    return (async () => {
      try {
        while (!this._closed && this.port.readable) {
          // Re-use whatever reader was set up by open() the first
          // time, then on subsequent loop iterations acquire a
          // fresh one (the previous stream is in the "errored"
          // state and the port has already given us a new readable).
          if (!this._reader) {
            try {
              this._reader = this.port.readable.getReader();
            } catch (e) {
              if (this._closed) break;
              this.log(`[serial] could not reacquire reader: ${e.message || e} — pump exiting`);
              break;
            }
          }
          const reader = this._reader;
          try {
            while (!this._closed) {
              const result = await reader.read();
              if (result.done) break;
              if (result.value && result.value.length) {
                const chunk = TEXT_DECODER.decode(result.value);
                this._buffer += chunk;
                const hex = Array.from(result.value).map(b =>
                  b.toString(16).padStart(2, "0")).join(" ");
                this.log(`< [hex] ${hex}`);
                const lines = chunk.split(/\r?\n/).filter(Boolean);
                for (const ln of lines) this.log(`< ${ln}`);
                this._notifyWaiters();
              }
            }
          } catch (e) {
            if (this._closed) break;
            // Non-fatal error (framing / parity / overrun). Web
            // Serial errors the current readable, but `port.readable`
            // resolves to a *new* stream on next access, so we just
            // release this reader and loop to reacquire.
            this.log(`[serial] read pump non-fatal error: ${e.message || e} — reacquiring reader`);
          } finally {
            try { reader.releaseLock(); } catch (_) { /* ignore */ }
            this._reader = null;
          }
          // Brief breathing room before reacquiring so we don't
          // spin in a tight loop if the device keeps emitting bad
          // framing.
          if (!this._closed) {
            await new Promise(r => setTimeout(r, 50));
          }
        }
      } finally {
        this._notifyWaiters();
      }
    })();
  }

  _notifyWaiters() {
    const ws = this._waiters;
    this._waiters = [];
    for (const w of ws) {
      try { w(); } catch (_) { /* ignore */ }
    }
  }

  /**
   * Tear down the port fully. Order matters: cancel the reader (which
   * unblocks the pump's pending ``read()``), AWAIT that, then release
   * locks and close. Without awaiting the cancel the subsequent
   * ``port.close()`` raises "The port is already open." intermittently
   * and leaves the hardware locked on macOS/Linux.
   */
  async close() {
    if (this._closed) return;
    this._closed = true;
    this._notifyWaiters();
    const reader = this._reader;
    const writer = this._writer;
    this._reader = null;
    this._writer = null;
    if (reader) {
      try { await reader.cancel(); } catch (_) { /* ignore */ }
      try { reader.releaseLock(); } catch (_) { /* ignore */ }
    }
    if (writer) {
      try { writer.releaseLock(); } catch (_) { /* ignore */ }
    }
    // Wait for the pump to settle so we don't double-close on Linux.
    try { await this._pumpDone; } catch (_) { /* ignore */ }
    try { await this.port.close(); } catch (_) { /* ignore */ }
    this.log("[serial] closed");
  }

  async writeRaw(text, { logEcho = true } = {}) {
    if (this._closed) throw new Error("port is closed");
    const bytes = TEXT_ENCODER.encode(text);
    if (logEcho) {
      const hex = Array.from(bytes).map(b =>
        b.toString(16).padStart(2, "0")).join(" ");
      this.log(`> ${text.replace(/\r/g, "\\r").replace(/\n/g, "\\n")} [${hex}]`);
    }
    await this._writer.write(bytes);
  }

  /**
   * Wait until ``predicate(buf)`` returns truthy or ``timeoutMs``
   * elapses. The predicate is re-evaluated whenever the background
   * pump appends new data, so latency is bounded only by the pump's
   * read() resolution time (which is essentially the OS-level USB
   * latency on the bridge — typically <50 ms).
   */
  async readUntil(predicate, { timeoutMs = 3000, signal } = {}) {
    const deadline = Date.now() + timeoutMs;
    while (true) {
      if (signal && signal.aborted) throw new Error("aborted");
      if (this._closed) throw new Error("port closed during read");
      const m = predicate(this._buffer);
      if (m) {
        const idx = (typeof m === "object" && m.index !== undefined)
          ? m.index + (m[0] ? m[0].length : 0)
          : this._buffer.indexOf(m);
        const matched = (typeof m === "object" && m[0] !== undefined) ? m[0] : m;
        if (idx >= 0) this._buffer = this._buffer.slice(idx);
        return matched;
      }
      const now = Date.now();
      if (now >= deadline) {
        throw new Error(`read timed out (looking for predicate, last 64 chars: ${
          this._buffer.slice(-64).replace(/\r/g, "\\r").replace(/\n/g, "\\n")})`);
      }
      // Sleep until either new data arrives, ``signal`` aborts, or a
      // short safety tick elapses (so we re-check timeout/abort).
      await new Promise((resolve) => {
        const remaining = deadline - Date.now();
        const t = setTimeout(resolve, Math.min(250, Math.max(20, remaining)));
        const cleanup = () => {
          clearTimeout(t);
          if (signal) signal.removeEventListener("abort", onAbort);
        };
        const wake = () => { cleanup(); resolve(); };
        const onAbort = () => wake();
        if (signal) {
          if (signal.aborted) return wake();
          signal.addEventListener("abort", onAbort, { once: true });
        }
        this._waiters.push(wake);
      });
    }
  }

  /**
   * Enter AT command mode using the DR404's escape sequence:
   *   0. 250 ms of UART silence (required by the firmware before
   *      the `+++` escape is recognised).
   *   1. Send `+++` (no CR/LF).
   *   2. Wait for the device to emit `a`.
   *   3. Send `a` (no CR/LF).
   *   4. Wait for `+ok`.
   * On failure of the `+++a` dance we send a bare `AT\r\n` and accept
   * any structured reply (``+ok`` *or* ``+ERR=…`` *or* ``ERROR``) as
   * proof the AT parser is alive — i.e. we ARE already in command
   * mode. iter150AZ.119
   */
  async enterAtMode({ timeoutMs = 3000, signal } = {}) {
    this._buffer = "";
    await sleep(250, signal);
    try {
      await this.writeRaw("+++", { logEcho: true });
      await this.readUntil(buf => buf.includes("a") ? "a" : null,
                            { timeoutMs, signal });
      await this.writeRaw("a", { logEcho: true });
      await this.readUntil(buf => buf.includes("+ok") ? "+ok" : null,
                            { timeoutMs, signal });
      this.log("[serial] entered AT mode");
      return;
    } catch (e) {
      if (e && e.message === "aborted") throw e;
      this.log(`[serial] +++a handshake failed: ${e.message} — probing for already-in-AT-mode`);
    }
    this._buffer = "";
    await this.writeRaw("AT\r\n", { logEcho: true });
    await this.readUntil(buf => /(\+?ok|\+?ERR|ERROR)/i.test(buf) ? "ack" : null,
                          { timeoutMs, signal });
    this.log("[serial] device was already in AT mode (probe acknowledged)");
  }

  /**
   * Poll the gateway with `+++` until the AT-mode handshake succeeds
   * (the device just came back from RELD / AT+Z and is now in
   * transparent mode again).
   */
  async pollUntilAtMode({ totalMs = 30000, intervalMs = 1500, signal,
                            log: extLog } = {}) {
    const start = Date.now();
    const end   = start + totalMs;
    const log   = extLog || this.log;
    let attempt = 0;
    while (Date.now() < end) {
      if (signal && signal.aborted) throw new Error("aborted");
      attempt++;
      const elapsed = ((Date.now() - start) / 1000).toFixed(1);
      this._buffer = "";
      try {
        await this.writeRaw("+++", { logEcho: true });
        await this.readUntil(buf => buf.includes("a") ? "a" : null,
          { timeoutMs: Math.min(1500, end - Date.now()), signal });
        await this.writeRaw("a", { logEcho: true });
        await this.readUntil(buf => buf.includes("+ok") ? "+ok" : null,
          { timeoutMs: 2000, signal });
        log(`[serial] gateway back online after ${elapsed}s (attempt ${attempt})`);
        return;
      } catch (e) {
        if (e && e.message === "aborted") throw e;
        log(`[serial] ping ${attempt} @ ${elapsed}s — no reply yet`);
        const remaining = end - Date.now();
        if (remaining > 0) {
          await sleep(Math.min(intervalMs, remaining), signal);
        }
      }
    }
    throw new Error(`gateway did not respond to +++ within ${totalMs / 1000}s`);
  }

  /**
   * Send an AT command and wait for `+ok` / `ok` / `+ERR` / `ERROR`.
   * Default timeout is 10 s — flash-writing commands (FAPSTA, WMODE,
   * REGEN, REGUSR, …) take noticeably longer than 3 s, and reboot
   * triggers (RELD, Z) can take 30 s or more. Callers should
   * override ``timeoutMs`` for the long-running ones explicitly.
   */
  async sendAt(command, { timeoutMs = 10000, signal } = {}) {
    if (!command || !command.startsWith("AT")) {
      throw new Error(`refusing to send non-AT command: ${command}`);
    }
    this._buffer = "";
    await this.writeRaw(command + "\r\n");
    const m = await this.readUntil(buf => {
      // CRITICAL: require an actual `\r` or `\n` terminator (NOT
      // end-of-buffer `$`). Otherwise a chunked response that lands
      // in two reads (e.g. "+ok=D4AD208272" then "68\r\n") matches
      // greedily on the first read and we return a TRUNCATED MAC.
      // iter150AZ.122
      //
      // iter150AZ.128 — The original anchor `(^|[\r\n])` REQUIRED
      // start-of-buffer or a newline immediately before `+ok`. But
      // the DR404 frequently echoes the AT command back, leaves
      // residual binary noise, or emits stray bytes around boot
      // transitions. In those cases the byte immediately before
      // `+ok=` is something like ` ` / `\xfe` / a letter from the
      // echo — which fails the strict anchor and causes verify-
      // stage `AT+REGUSR` / `AT+HEARTDT` to time out even when the
      // device clearly responded with `+ok=<value>\r\n`. We instead
      // use a non-word lookbehind: `+ok` must not be a continuation
      // of an alphanumeric token (so we never mis-match `BookOK`
      // etc.) but is allowed after any non-word byte.
      const okRe  = /(?<![A-Za-z0-9_])(\+?ok)(=[^\r\n]*)?(?=\r|\n)/i;
      const errRe = /(?<![A-Za-z0-9_])(\+ERR[^\r\n]*|ERROR[^\r\n]*)(?=\r|\n)/i;
      const okM   = buf.match(okRe);
      const errM  = buf.match(errRe);
      const cands = [];
      if (okM)  cands.push({ kind: "ok",  index: okM.index,  full: okM[0] });
      if (errM) cands.push({ kind: "err", index: errM.index, full: errM[0] });
      if (cands.length === 0) return null;
      cands.sort((a, b) => a.index - b.index);
      const first = cands[0];
      let end = first.index + first.full.length;
      while (end < buf.length && (buf[end] === "\r" || buf[end] === "\n")) end++;
      return { 0: buf.slice(first.index, end), index: first.index };
    }, { timeoutMs, signal });
    if (/^\+?ERR|^ERROR/i.test(m)) {
      throw new Error(`device returned error: ${m.trim()}`);
    }
    return m.replace(/^\+?ok=?/i, "").trim();
  }

  /**
   * Send an AT command whose `+ok=` reply spans multiple lines
   * (e.g. ``AT+WSCAN`` returns a CSV-style hotspot table). Single-
   * line ``sendAt`` returns immediately on ``+ok=\n`` and throws
   * away the table that arrives afterwards — this variant waits
   * for the canonical USR end-of-response marker (>= 4 consecutive
   * CR/LF bytes; row separators are only 2). iter150AZ.125
   */
  async sendAtMultiline(command, { timeoutMs = 15000, signal } = {}) {
    if (!command || !command.startsWith("AT")) {
      throw new Error(`refusing to send non-AT command: ${command}`);
    }
    this._buffer = "";
    await this.writeRaw(command + "\r\n");
    const m = await this.readUntil(buf => {
      // Error check first — surfaces +ERR=-N immediately.
      const errRe = /(?<![A-Za-z0-9_])(\+ERR[^\r\n]*|ERROR[^\r\n]*)(?=\r|\n)/i;
      const errM = buf.match(errRe);
      if (errM) {
        let end = errM.index + errM[0].length;
        while (end < buf.length && (buf[end] === "\r" || buf[end] === "\n")) end++;
        return { 0: buf.slice(errM.index, end), index: errM.index };
      }
      // Multi-line +ok response — find `+ok=` and the end marker.
      // 4+ consecutive CR/LF chars only happen at end-of-response
      // (single row terminators are only 2 chars). iter150AZ.128 —
      // use a non-word lookbehind for the same reason as ``sendAt``.
      const okM = buf.match(/(?<![A-Za-z0-9_])(\+?ok=?)([\s\S]*?)([\r\n]{4,})/i);
      if (!okM) return null;
      return { 0: okM[0], index: okM.index };
    }, { timeoutMs, signal });
    if (/^\+?ERR|^ERROR/i.test(m)) {
      throw new Error(`device returned error: ${m.trim()}`);
    }
    // Strip the `+ok=` prefix and any leading/trailing newlines.
    return m.replace(/^\+?ok=?/i, "").trim();
  }
}


/**
 * Try a list of baud rates until ``enterAtMode`` succeeds. Returns the
 * connected :class:`Dr404Serial`. Closes the failing port between
 * attempts and rethrows if no rate works. ``signal`` is forwarded so
 * the dialog can cancel the whole sweep on dismissal.
 */
export async function connectAndEnterAtMode(port, log,
                                              rates = COMMON_BAUD_RATES,
                                              { signal } = {}) {
  let lastErr = null;
  for (const baud of rates) {
    if (signal && signal.aborted) throw new Error("aborted");
    const dev = new Dr404Serial({ port, log, baudRate: baud });
    try {
      await dev.open(baud);
      await dev.enterAtMode({ timeoutMs: 1800, signal });
      return dev;
    } catch (e) {
      log(`[serial] AT mode @ ${baud} failed: ${e.message}`);
      lastErr = e;
      try { await dev.close(); } catch (_) { /* ignore */ }
      if (e && e.message === "aborted") throw e;
    }
  }
  throw lastErr || new Error("could not enter AT mode at any baud rate");
}
